import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { StatCard } from "../../design-system/StatCard";
import { Icon } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc } from "../../ipc/IpcProvider";
import type { ConsolidatedResult } from "../../ipc/types";
import "../dashboard/dashboard.css";

/**
 * Panel consolidado "Todas las cuentas" (MC4). Agrega los KPIs clave de cada cuenta de Mercado Libre
 * (personal, empresa…) en una sola vista, con totales. Solo lectura: para operar, cambia de cuenta
 * con el selector de la barra superior.
 */
export function AccountsScreen(): JSX.Element {
  const client = useIpc();
  const [data, setData] = useState<ConsolidatedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    const r = await client.getConsolidated();
    if (r.ok) { setData(r.value); setError(null); } else setError(r.error.message);
  };
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState />;

  const connected = data.accounts.filter((a) => a.connected).length;

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="marketplace" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Todas las cuentas</h1>
              <p className="pagehead__sub">Resumen consolidado de tus cuentas de Mercado Libre. Para operar una, cámbiala en el selector de arriba.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        <StatCard label="Cuentas" value={String(data.accounts.length)} icon="marketplace" accent="yellow" />
        <StatCard label="Conectadas" value={String(connected)} icon="check" accent="green" />
        <StatCard label="Ingreso total (K$)" value={data.totals.ingreso.toLocaleString("es-CL")} icon="sales" accent="blue" />
        <StatCard label="Órdenes totales" value={data.totals.ordenes.toLocaleString("es-CL")} icon="finance" accent="red" />
      </div>

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Por cuenta" subtitle="Ingreso, órdenes y stock de cada cuenta." action={<Badge tone="brand" dot>Consolidado</Badge>} />
        <div className="card__body" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>Cuenta</th><th>Estado</th><th>Ingreso (K$)</th><th>Órdenes</th><th>Stock</th></tr></thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.id}>
                  <td className="strong">{a.nickname}</td>
                  <td><Badge tone={a.connected ? "success" : "neutral"} dot>{a.connected ? "Conectada" : "Sin conectar"}</Badge></td>
                  <td className="strong">{a.ingreso.toLocaleString("es-CL")}</td>
                  <td className="strong">{a.ordenes.toLocaleString("es-CL")}</td>
                  <td className="strong">{a.stock.toLocaleString("es-CL")}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid var(--border)" }}>
                <td className="strong">Total</td>
                <td />
                <td className="strong">{data.totals.ingreso.toLocaleString("es-CL")}</td>
                <td className="strong">{data.totals.ordenes.toLocaleString("es-CL")}</td>
                <td className="strong">{data.totals.stock.toLocaleString("es-CL")}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
